import api from './api';

// Definición de la interfaz Project (la mantenemos igual)
export interface Project {
  id: string;
  name: string;
  iata_code?: string;
  icao_code?: string;
  analysis_status: 'COMPLETED' | 'PROCESSING' | 'DRAFT' | 'FAILED' | 'PENDING';
  created_at: string;
  updated_at?: string;
  has_file: boolean;
  description?: string;

  // Phase 1: Creation Params
  total_pax_last_year?: number;
  pax_capacity?: number;
  total_mov_last_year?: number;
  runway_capacity?: number;
  ptbc_capacity?: number;
  runway_config?: any;

  // Phase 2: Configuration
  sme_parameters?: Record<string, any>;
  sectors_config?: any[];

  // Results
  analysis_results?: any;
  calculated_inputs?: any;
  demand_curve?: number[];
}

/** Estado de procedencia de un campo auto-rellenado desde el dataset. */
export type ProvenanceStatus = 'modelled' | 'missing' | 'defaulted' | 'default';

export interface ProvenanceEntry {
  status: ProvenanceStatus;
  detail?: string;
  source?: string;
}

/**
 * Respuesta de POST /projects/parse-dataset. `aircap_config` solo trae las
 * claves que el parser halló (inventory/service_sec/security_lane_paxh/shares/
 * pax_per_movement/avg_seats/load_factor). Ver terminal_ingest.py.
 */
export interface ParseDatasetResult {
  aircap_config: {
    inventory?: Record<string, number>;
    service_sec?: Record<string, number>;
    security_lane_paxh?: number;
    shares?: Record<string, number>;
    pax_per_movement?: number;
    avg_seats?: number;
    load_factor?: number;
  };
  provenance: {
    subsystems: Record<string, ProvenanceEntry>;
    rates: Record<string, ProvenanceEntry>;
    shares: ProvenanceEntry;
    pax_per_movement: ProvenanceEntry;
    modelled_subsystems: string[];
    missing_subsystems: string[];
    partial: boolean;
    reason?: string;
  };
  pax_per_movement?: number;
  sheets_detected: string[];
}

export const projectsService = {
  // --- GESTIÓN BÁSICA (CRUD) ---

  // Obtener lista de proyectos
  async getAll(): Promise<Project[]> {
    try {
      const response = await api.get('/projects/');
      return response.data;
    } catch (error) {
      console.warn('Backend no disponible, usando datos mock.');
      return [
        {
          id: '1',
          name: 'Análisis Operativo LEBL',
          iata_code: 'BCN',
          icao_code: 'LEBL',
          analysis_status: 'COMPLETED',
          created_at: '2023-10-20',
          has_file: true,
          description: 'Análisis de capacidad verano 2024'
        },
        {
          id: '2',
          name: 'Expansión LEMD T4',
          iata_code: 'MAD',
          icao_code: 'LEMD',
          analysis_status: 'PROCESSING',
          created_at: '2023-10-22',
          has_file: true,
          description: 'Simulación de nuevas puertas'
        }
      ];
    }
  },

  // Obtener un proyecto
  async getOne(id: string): Promise<Project | null> {
    try {
      const response = await api.get(`/projects/${id}`);
      return response.data;
    } catch (error) {
      console.warn("Error fetching project, returning mock");
      return {
        id: id,
        name: 'Proyecto Demo Detallado',
        iata_code: 'DEM',
        icao_code: 'DEMO',
        analysis_status: 'DRAFT',
        created_at: new Date().toISOString(),
        has_file: false,
        description: 'Proyecto cargado en modo diseño',
        analysis_results: {
          apcas: { rac_capacity: 0, bci_index: 0 },
          calculated_inputs: { w_workload_dyn: 0, am_index_stat: 1.0 },
          demand_curve: []
        }
      };
    }
  },

  // Crear proyecto
  async create(data: Partial<Project>): Promise<Project> {
    const response = await api.post('/projects/', data);
    return response.data;
  },

  // Actualizar proyecto
  async update(id: string, data: Partial<Project>): Promise<Project> {
    const response = await api.put(`/projects/${id}`, data);
    return response.data;
  },

  // Eliminar proyecto
  async delete(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  // --- GESTIÓN DE ARCHIVOS E INGESTA (AQUÍ ESTÁ LA CORRECCIÓN) ---

  /**
   * Pre-relleno del wizard: parsea un dataset unificado SIN persistir nada y
   * devuelve la config AirCap auto-construida + su provenance, para pre-rellenar
   * (editable) los campos de terminal en vez de teclearlos a mano. El MISMO
   * fichero se envía luego a uploadFile() tras crear el proyecto para lanzar el
   * pipeline (ver NewProjectPage.handleSubmit). Stateless en el backend
   * (POST /projects/parse-dataset).
   */
  async parseDataset(file: File): Promise<ParseDatasetResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/projects/parse-dataset', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000, // 2 min — el parseo lee las hojas de terminal de un xlsx grande
    });
    return response.data;
  },

  /**
   * Sube un archivo local (Excel/CSV) al backend.
   *
   * `timeBasis` declara si las horas del datasheet son UTC o ya son hora local
   * del aeropuerto. No se puede deducir del dato ("23:30" es idéntico en ambas
   * bases) y el nombre de columna *_UTC es una suposición, no una prueba: si se
   * tratan horas locales como UTC se les aplica el offset del aeropuerto por
   * segunda vez y la ventana nocturna (NOF) se clasifica sobre horas que nunca
   * existieron. Por eso lo declara quien aporta el fichero.
   */
  async uploadFile(projectId: string, file: File, timeBasis: 'utc' | 'local' = 'utc'): Promise<any> {
    const formData = new FormData();
    // Usamos 'file' para coincidir con el alias del backend
    formData.append('file', file);
    formData.append('time_basis', timeBasis);

    console.log("[Upload] Subiendo archivo...", file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`, `time_basis=${timeBasis}`);

    // Extended timeout for large files - 2 minutes
    const response = await api.post(`/projects/${projectId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000,  // 2 minutes for large Excel files
    });
    return response.data;
  },

  /**
   * Conecta una URL de Google Sheets al proyecto.
   */
  async connectSheet(projectId: string, url: string, timeBasis: 'utc' | 'local' = 'utc'): Promise<any> {
    const formData = new FormData();
    formData.append('url', url);
    // A Google Sheet is the same datasheet by another route: its times need the
    // same UTC/local declaration (see uploadFile).
    formData.append('time_basis', timeBasis);

    // Para URL también usamos form-data
    const response = await api.post(`/projects/${projectId}/google-sheet`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  /**
   * Consulta el progreso de una tarea de Celery.
   */
  async getTaskStatus(taskId: string): Promise<any> {
    const response = await api.get(`/projects/tasks/${taskId}`);
    return response.data;
  },

  /**
   * Genera API Key (Simulado)
   */
  async getIntegrationDetails(projectId: string): Promise<{ endpoint: string, apiKey: string }> {
    return {
      endpoint: `${api.defaults.baseURL || 'https://api.dacams.com'}/ingest/upload/${projectId}`,
      apiKey: `sk_live_${projectId}_${Math.random().toString(36).substring(7)}`
    };
  },

  /**
   * Re-ejecuta el análisis con archivo existente y configuración actual.
   * Ideal para probar diferentes parámetros SME sin re-subir el archivo.
   */
  async reanalyze(projectId: string): Promise<any> {
    console.log("🔄 Re-ejecutando análisis con configuración actual...");
    const response = await api.post(`/projects/${projectId}/reanalyze`);
    return response.data;
  },

  /**
   * ADOSA what-if: recompute the staffing scenario on the server from the roster
   * form. Returns the audited result (with provenance); the browser no longer
   * runs the staffing formulas itself.
   */
  async recalculateAdosa(projectId: string, params: Record<string, any>): Promise<any> {
    const response = await api.post(`/projects/${projectId}/adosa/recalculate`, params);
    return response.data;
  },

  /**
   * Detiene el análisis en curso.
   */
  async stopAnalysis(projectId: string): Promise<any> {
    const response = await api.post(`/projects/${projectId}/stop`);
    return response.data;
  },

  /**
   * Re-parse AIP AD2 document and update project aerodrome data.
   */
  async reparseAip(projectId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/aip/reparse/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000, // 10 min for large PDFs
    });
    return response.data;
  },

  // --- FLIGHT TRACKS (FlightAware AeroAPI) ---

  /**
   * Download flight tracks from FlightAware for a date range.
   * Returns a task_id for progress polling.
   */
  async downloadFlightTracks(
    projectId: string,
    startDate: string,
    endDate: string,
    specificDates?: string[],
  ): Promise<any> {
    const label = specificDates?.length ? `${specificDates.length} specific days` : `${startDate} to ${endDate}`;
    console.log(`✈️ Requesting flight tracks: ${label}`);
    const response = await api.post(`/projects/${projectId}/flight-tracks/download`, {
      start_date: startDate,
      end_date: endDate,
      radius_km: 5.0,
      ...(specificDates?.length ? { specific_dates: specificDates } : {}),
    });
    return response.data;
  },

  /**
   * Estimate the cost of downloading flight tracks.
   */
  async estimateFlightTracksCost(projectId: string, startDate: string, endDate: string): Promise<any> {
    const response = await api.get(`/projects/${projectId}/flight-tracks/estimate`, {
      params: { start_date: startDate, end_date: endDate },
    });
    return response.data;
  },

  // --- GOSCORE BENCHMARKING ---

  /**
   * Upload a GOscore TGT Tool CSV export for benchmarking comparison.
   * When date_from and date_to are given, AODB data is filtered to the same period.
   * Returns comparison results between AODB KPIs and GOscore observed data.
   */
  async uploadGoscoreBenchmark(
    projectId: string,
    csvFile: File,
    dateFrom?: string,
    dateTo?: string
  ): Promise<any> {
    const formData = new FormData();
    formData.append('file', csvFile);
    if (dateFrom) formData.append('date_from', dateFrom);
    if (dateTo) formData.append('date_to', dateTo);

    console.log("📊 Uploading GOscore CSV for benchmarking...", csvFile.name,
                dateFrom ? `Period: ${dateFrom} → ${dateTo}` : 'No period filter');

    const response = await api.post(`/projects/${projectId}/goscore-benchmark`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 min — 83K row AODB period filtering + comparison
    });
    return response.data;
  },

  // --- APCAP GENERATION ---

  /**
   * Generate APCAP (Apron Capacity Plan) Word document using AI Agent.
   * Returns a Blob for direct download.
   */
  async generateApcap(projectId: string, language: string = 'es'): Promise<Blob> {
    const response = await api.get(`/projects/${projectId}/report`, {
      params: { format: 'apcap', language },
      responseType: 'blob',
      timeout: 180000, // 3 minutes for AI narrative generation
    });
    return response.data;
  },
};